import { useCallback, useEffect, useRef, useState } from 'react';
import type { EligibleTour } from '../types';

export interface UseContextualTourReturn {
  isPromptVisible: boolean;
  promptTour: EligibleTour | null;
  acceptPrompt: () => void;
  declinePrompt: () => void;
}

export function useContextualTour(
  contextualTours: EligibleTour[],
  currentPathname: string,
  onStartTour?: (slug: string) => void,
  onDismissTour?: (slug: string) => void
): UseContextualTourReturn {
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [promptTour, setPromptTour] = useState<EligibleTour | null>(null);
  const shownSlugsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const matched = contextualTours.find(
      (t) =>
        t.contextRoute !== null &&
        t.contextRoute === currentPathname &&
        !shownSlugsRef.current.has(t.slug)
    );

    if (matched) {
      shownSlugsRef.current.add(matched.slug);
      setPromptTour(matched);
      setIsPromptVisible(true);
    } else if (isPromptVisible) {
      setIsPromptVisible(false);
      setPromptTour(null);
    }
  }, [currentPathname, contextualTours]);

  const acceptPrompt = useCallback(() => {
    const tour = promptTour;
    setIsPromptVisible(false);
    if (tour && onStartTour) {
      onStartTour(tour.slug);
    }
  }, [promptTour, onStartTour]);

  const declinePrompt = useCallback(() => {
    const tour = promptTour;
    setIsPromptVisible(false);
    if (tour && onDismissTour) {
      onDismissTour(tour.slug);
    }
  }, [promptTour, onDismissTour]);

  return {
    isPromptVisible,
    promptTour,
    acceptPrompt,
    declinePrompt,
  };
}
